import {
  presignUploads,
  confirmUploads,
  putToPresignedUrl,
  PresignUpload,
} from "../api"
import {
  compressImage,
  isHeicFile,
  transcodeHeicToJpeg,
} from "./imageCompression"

// Orchestrates the direct-to-R2 upload flow for a batch of files belonging to
// one event / user / folder:
//   1) POST /uploads/presign to reserve fileIds + presigned PUT URLs
//   2) PUT bytes straight to those URLs (photos: original + compressed + index;
//      videos: original only), with retries and per-file progress
//   3) POST /uploads/confirm for the files whose bytes made it into R2

const VIDEO_EXTS = ["mp4", "mov", "m4v", "avi", "mkv", "webm", "3gp"]
const PUT_RETRIES = 3

export type FileStatus =
  | "pending"
  | "compressing"
  | "uploading"
  | "confirming"
  | "success"
  | "failed"
  | "skipped"
  | "unsupported"

export interface FileProgress {
  clientRef: string
  name: string
  status: FileStatus
  progress: number
  error?: string
}

export interface PipelineResult {
  confirmedFileIds: string[]
  failedFileIds: string[]
  skipped: Array<{ name: string; reason: string }>
}

interface PipelineFile {
  clientRef: string
  file: File
  ext: string
  isVideo: boolean
}

// A single PUT to a presigned URL, with mutable byte counters used to
// aggregate per-file progress across the (up to 3) parallel PUTs.
interface UploadPart {
  url: string
  body: Blob
  contentType: string
  loaded: number
  total: number
}

function extname(name: string): string {
  const i = name.lastIndexOf(".")
  return i >= 0 ? name.slice(i + 1).toLowerCase() : ""
}

function replaceExtension(name: string, ext: string): string {
  const i = name.lastIndexOf(".")
  return `${i >= 0 ? name.slice(0, i) : name}.${ext}`
}

async function putPartWithRetry(
  part: UploadPart,
  reportProgress: () => void
): Promise<void> {
  let lastErr: unknown
  // Presigned URLs are valid ~15 min, so a transient PUT failure is worth
  // retrying against the same URL.
  for (let attempt = 1; attempt <= PUT_RETRIES; attempt++) {
    try {
      part.loaded = 0
      await putToPresignedUrl(
        part.url,
        part.body,
        part.contentType,
        (loaded, total) => {
          part.loaded = loaded
          part.total = total || part.total
          reportProgress()
        }
      )
      part.loaded = part.total
      reportProgress()
      return
    } catch (err) {
      lastErr = err
    }
  }
  throw lastErr
}

export async function runUploadPipeline(
  rawFiles: File[],
  ctx: { eventId: string; userId: string; folderId: string | null },
  onUpdate: (clientRef: string, patch: Partial<FileProgress>) => void
): Promise<PipelineResult> {
  const skipped: PipelineResult["skipped"] = []

  // 0) Normalize HEIC/HEIF to JPEG up front so the stored ORIGINAL is
  // web-viewable on every platform (desktop hands us raw HEIC; iOS already
  // transcodes to JPEG at the picker). After this, ext/fileName/sizeBytes and
  // every uploaded copy are JPEG, and compression never re-runs heic2any.
  const files: PipelineFile[] = []
  for (let i = 0; i < rawFiles.length; i++) {
    const clientRef = `file-${i}`
    let file = rawFiles[i]
    let ext = extname(file.name)

    if (isHeicFile(file, ext)) {
      onUpdate(clientRef, { status: "compressing" })
      try {
        const jpeg = await transcodeHeicToJpeg(file)
        file = new File([jpeg], replaceExtension(file.name, "jpg"), {
          type: "image/jpeg",
          lastModified: file.lastModified,
        })
        ext = "jpg"
      } catch (err: any) {
        // TODO: a server-side fallback should handle HEIC we can't decode.
        const reason = err?.message || "HEIC conversion failed"
        skipped.push({ name: rawFiles[i].name, reason })
        onUpdate(clientRef, { status: "skipped", error: reason })
        continue
      }
    }

    const isVideo = file.type.startsWith("video/") || VIDEO_EXTS.includes(ext)
    files.push({ clientRef, file, ext, isVideo })
  }

  if (files.length === 0) {
    return { confirmedFileIds: [], failedFileIds: [], skipped }
  }

  // 1) Reserve fileIds + presigned URLs. sizeBytes is the ORIGINAL size (quota).
  const presignResp = await presignUploads({
    eventId: ctx.eventId,
    userId: ctx.userId,
    folderId: ctx.folderId,
    files: files.map((f) => ({
      clientRef: f.clientRef,
      fileName: f.file.name,
      ext: f.ext,
      sizeBytes: f.file.size,
    })),
  })
  if (presignResp?.status !== "OK" || !presignResp?.body?.uploads) {
    throw new Error(presignResp?.error || "Failed to reserve uploads")
  }

  const uploads: PresignUpload[] = presignResp.body.uploads
  const byRef = new Map(uploads.map((u) => [u.clientRef, u]))

  // Files the server silently dropped from `uploads` are unsupported types.
  for (const f of files) {
    if (!byRef.has(f.clientRef)) {
      onUpdate(f.clientRef, {
        status: "unsupported",
        error: "Unsupported file type",
      })
    }
  }

  const confirmable: Array<{ fileId: string; fileName: string; ext: string }> =
    []

  // 2) Upload each file's bytes. Files run concurrently; each file's PUTs run
  // in parallel.
  await Promise.all(
    files.map(async (f) => {
      const up = byRef.get(f.clientRef)
      if (!up) return // unsupported, already flagged

      // Photos need compressed bytes for the compressed + index PUTs.
      let compressed: Blob | null = null
      let compressedMime = "image/jpeg"
      if (!f.isVideo) {
        onUpdate(f.clientRef, { status: "compressing" })
        const result = await compressImage(f.file, f.ext)
        if (result.status === "skipped") {
          // Large-image / decode failures must not go through this flow.
          skipped.push({ name: f.file.name, reason: result.reason })
          onUpdate(f.clientRef, { status: "skipped", error: result.reason })
          return
        }
        compressed = result.image.blob
        compressedMime = result.image.mime
      }

      const parts: UploadPart[] = [
        {
          url: up.originalUploadUrl,
          body: f.file,
          contentType: f.file.type || "application/octet-stream",
          loaded: 0,
          total: f.file.size,
        },
      ]
      if (!f.isVideo && compressed) {
        if (up.compressedUploadUrl) {
          parts.push({
            url: up.compressedUploadUrl,
            body: compressed,
            contentType: compressedMime,
            loaded: 0,
            total: compressed.size,
          })
        }
        // Same compressed bytes feed the face-indexing pipeline.
        if (up.indexUploadUrl) {
          parts.push({
            url: up.indexUploadUrl,
            body: compressed,
            contentType: compressedMime,
            loaded: 0,
            total: compressed.size,
          })
        }
      }

      const totalBytes = parts.reduce((s, p) => s + (p.total || 0), 0) || 1
      const reportProgress = () => {
        const loaded = parts.reduce((s, p) => s + p.loaded, 0)
        onUpdate(f.clientRef, {
          progress: Math.min(100, Math.round((loaded * 100) / totalBytes)),
        })
      }

      onUpdate(f.clientRef, { status: "uploading", progress: 0 })
      try {
        await Promise.all(parts.map((p) => putPartWithRetry(p, reportProgress)))
        confirmable.push({
          fileId: up.fileId,
          fileName: f.file.name,
          ext: f.ext,
        })
        onUpdate(f.clientRef, { status: "confirming", progress: 100 })
      } catch (err: any) {
        onUpdate(f.clientRef, {
          status: "failed",
          error: err?.message || "Upload failed",
        })
      }
    })
  )

  // 3) Confirm the files whose bytes are in R2. The backend verifies the
  // objects exist before persisting, so a file only "counts" once it's in
  // confirmedFileIds.
  let confirmedFileIds: string[] = []
  let failedFileIds: string[] = []
  if (confirmable.length > 0) {
    const confirmResp = await confirmUploads({
      eventId: ctx.eventId,
      userId: ctx.userId,
      folderId: ctx.folderId,
      files: confirmable,
    })
    if (confirmResp?.status !== "OK") {
      throw new Error(confirmResp?.error || "Failed to confirm uploads")
    }
    confirmedFileIds = confirmResp.body?.confirmedFileIds || []
    failedFileIds = confirmResp.body?.failedFileIds || []
  }

  // Reconcile confirm results back onto the per-file rows.
  const confirmedSet = new Set(confirmedFileIds)
  const failedMap = new Map<string, string>() // fileId -> reason ("<id>:<reason>")
  for (const entry of failedFileIds) {
    const [id, ...rest] = entry.split(":")
    failedMap.set(id, rest.join(":") || "Failed")
  }
  for (const f of files) {
    const up = byRef.get(f.clientRef)
    if (!up) continue
    if (confirmedSet.has(up.fileId)) {
      onUpdate(f.clientRef, { status: "success", progress: 100 })
    } else if (failedMap.has(up.fileId)) {
      onUpdate(f.clientRef, {
        status: "failed",
        error: failedMap.get(up.fileId),
      })
    }
  }

  return { confirmedFileIds, failedFileIds, skipped }
}
